import {Component, Input, OnChanges, OnDestroy, SimpleChanges, TemplateRef} from '@angular/core';
import {
    AbstractControl,
    ControlValueAccessor,
    FormBuilder,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    UntypedFormGroup,
    ValidationErrors,
    Validators
} from "@angular/forms";
import {filter, map, startWith} from "rxjs/operators";
import {
    arrayIsSet,
    deepClone,
    KeyStringValue,
    objectIsSet,
    stringIsSet
} from "../../providers/service/global-variables.service";
import {BehaviorSubject, combineLatest, Observable, of, Subscription} from "rxjs";
import {MatDialog} from "@angular/material/dialog";
import {dataForm} from "../auto-complete-form/auto-complete-form.component";
import {OptionalParameterInfo} from "../../ParseClass/SensorModel.Parse";
import {typeFormValue} from "../../configurationProperty/configurationPropertyUtils";
import {PossibleValuesFormField} from "../multi-select-form-field/multi-select-form-field.component";

export type SpecialButton = {
    key: { traduction: string, registerKey: string },
    possibleValues: { key: string, values: PossibleValuesFormField[], type: typeFormValue, traduction: string, }[]
}

@Component({
    selector: 'app-key-values-field',
    templateUrl: './key-values-field.component.html',
    styleUrls: ['./key-values-field.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: KeyValuesFieldComponent
        },
        {
            provide: NG_VALIDATORS,
            multi: true,
            useExisting: KeyValuesFieldComponent
        }
    ],
    standalone: false
})
export class KeyValuesFieldComponent implements OnChanges, OnDestroy, ControlValueAccessor {
    @Input() title: string | undefined
    private possibleValuesEmit = new BehaviorSubject<OptionalParameterInfo[] | any[]>([])
    private possibleValues$ = this.possibleValuesEmit.asObservable()

    @Input() set possibleValues(values) {
        this.possibleValuesEmit.next(values)
    }

    get possibleValues() {
        return this.possibleValuesEmit.value
    }

    @Input() specials?: SpecialButton
    form: UntypedFormGroup;
    specialButtonForm: UntypedFormGroup;
    subscriptions = new Subscription();
    values$: Observable<KeyStringValue<string | number>>
    specialValues$: Observable<any>
    keyValuesInArray$: Observable<{ key: string, value: string | number | undefined }[] | undefined>
    numberKeyValues$: Observable<number>
    numberSpecialKeyValues$: Observable<number>
    disableAddButton$: Observable<boolean>
    disableAddSpecialButton$: Observable<boolean> = of(false)
    errorMessages$: Observable<{ keys: string[], message: string } | undefined>
    addingKey$: Observable<string | undefined>
    typeForm = typeFormValue
    defaultKeys

    constructor(private fb: FormBuilder,
                private matDialog: MatDialog) {
        this.form = fb.group({
            addingKey: [undefined, [Validators.required]],
            addingValue: [undefined, [Validators.required]],
            value: [undefined, [Validators.required]]
        })
        this.specialButtonForm = fb.group({currentValues: undefined});
        this.defaultKeys = Object.keys(this.specialButtonForm.value)
        this.values$ = (this.form.get('value')?.valueChanges as any).pipe(
            startWith(undefined),
            map(() => this.form.get('value')?.value)
        )

        this.keyValuesInArray$ = this.values$.pipe(
            map(values => {
                let arrayValues
                if (values != null) {
                    const keys = Object.keys(values)
                    if (arrayIsSet(keys)) {
                        arrayValues = keys.map(key => {
                            return {key, value: values[key]}
                        })
                    }
                }
                return arrayValues
            })
        )
        this.numberKeyValues$ = this.keyValuesInArray$.pipe(
            map(values => arrayIsSet(values) ? values.length : 0)
        )
        this.specialValues$ = this.specialButtonForm.get('currentValues')!.valueChanges.pipe(
            startWith(undefined),
            map(() => this.specialButtonForm.get('currentValues')!.value)
        )

        this.numberSpecialKeyValues$ = this.specialButtonForm.get('currentValues')!.valueChanges.pipe(
            startWith(undefined),
            map(values => arrayIsSet(values) ? values.length : 0)
        )
        const addingKey$ = (this.form.get('addingKey') as any).valueChanges.pipe(
            startWith(undefined),
            map(() => this.form.get('addingKey')?.value)
        );
        const addingValue$ = (this.form.get('addingValue') as any).valueChanges.pipe(
            startWith(undefined),
            map(() => this.form.get('addingValue')?.value)
        );
        this.disableAddButton$ = combineLatest([addingKey$, addingValue$]).pipe(
            map(([addingKey, addingValue]) => {
                return !stringIsSet(addingKey) || addingValue == null
            })
        )
        this.errorMessages$ = combineLatest([this.values$, addingKey$, addingValue$, this.possibleValues$]).pipe(
            filter(([values, addingKey, addingValue, possibleValues]) => {
                return arrayIsSet(possibleValues)
            }),
            map(([values, addingKey, addingValue, possibleValues]) => {
                let keys: string[] = []
                possibleValues.forEach((pValue: OptionalParameterInfo) => {
                    if (pValue.required) {
                        if (values == null || values[pValue.key] == null || (addingKey == pValue.key && addingValue == null)) {
                            keys.push(pValue.key)
                        }
                    }
                })
                if (arrayIsSet(keys)) {
                    return {message: 'alert.requiredKeys', keys}
                }
                return undefined
            })
        )
        this.addingKey$ = addingKey$;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.specials&&this.specials) {
            const specialKey = this.specials!.key.registerKey!
            if (this.specialButtonForm.get(specialKey) == null) {
                // const obj: any = this.specials?.possibleValues.reduce((prev, p) => {
                //     prev[p.key] = undefined
                //     return prev
                // }, {} as KeyStringValue<any>)
                this.specials?.possibleValues.forEach((p) => {
                    this.specialButtonForm.addControl(p.key, this.fb.nonNullable.control(undefined))
                })
            }
            Object.keys(this.specialButtonForm.value).forEach(key => {
                if (!this.defaultKeys.concat(this.specials!.possibleValues.map(p => p.key)).includes(key)) {
                    this.specialButtonForm.removeControl(key)
                }
            })

        }
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    onTouched: Function = () => {
    };

    registerOnChange(onChange: any) {
        const values$ = this.values$.pipe(
            map((values) => {
                if (objectIsSet(values)) {
                    return values;
                } else {
                    return;
                }
            })
        )
        const specialValue$ = this.specialValues$.pipe(
            map((values) => {
                if (arrayIsSet(values)) {
                    return values;
                } else {
                    return;
                }
            })
        )
        this.subscriptions.add(
            combineLatest(values$, specialValue$)
                .pipe(
                    map(([value, specialValue]) => {
                        let allValue;
                        if (value) {
                            allValue = deepClone(value)
                        }
                        if (arrayIsSet(specialValue)) {
                            if (allValue == null) {
                                allValue = {} as any
                            }
                            allValue[this.specials!.key.registerKey] = specialValue
                        }
                        return allValue
                    })
                )
                .subscribe(onChange)
        );

    }

    setDisabledState(disabled: boolean) {
        if (disabled) {
            this.form.disable();
        } else {
            this.form.enable();
        }
    }

    writeValue(value: any) {
        if (objectIsSet(value)) {
            let copyValue = deepClone(value);
            if (this.specials && this.specials!.key) {
                this.specialButtonForm.get('currentValues')?.setValue(copyValue[this.specials?.key.registerKey!])
                delete copyValue[this.specials?.key.registerKey!];
            }
            setTimeout(() => {
                this.form.get('value')?.setValue(copyValue)
            })
        } else {
            this.form.reset();
            this.specialButtonForm.reset();
        }
    }

    validate(control: AbstractControl): ValidationErrors | null {
        // if (!isNotNullOrUndefined(this.form.get('autoComplete')?.validator)) {
        //     this.form.get('autoComplete')?.setValidators(control.validator)
        //     this.form.get('autoComplete')?.updateValueAndValidity()
        // }
        if (this.form.valid) {
            return null
        } else {
            return this.form.errors!;
        }
    }

    addControlErrors(allErrors: any, controlName: string) {
        const errors = {...allErrors};
        const controlErrors = this.form.controls[controlName].errors;
        if (controlErrors) {
            errors[controlName] = controlErrors;
        }
        return errors;
    }

    registerOnTouched(onTouched: Function) {
        this.onTouched = onTouched;
    }

    addKeyValues(dialogComponent: TemplateRef<any>) {
        const dialogref = this.matDialog.open(dialogComponent, {
            maxWidth: '100vw', autoFocus: false,
            exitAnimationDuration: 0.1
        })
        dialogref.afterClosed().subscribe(() => {
            this.form.get('addingKey')?.reset();
            this.form.get('addingValue')?.reset();
        });
    }

    addKeyValue() {
        const key = this.form.get('addingKey')?.value;
        let value = this.form.get('addingValue')?.value;
        if (value != null) {
            const numberValue = parseFloat(value)
            if (!isNaN(numberValue)) {
                value = numberValue
            }
        }
        const currentValue = this.form.get('value')?.value != null ? {...this.form.get('value')?.value} : {};
        currentValue[key] = value;
        this.form.get('value')?.setValue(currentValue);
        this.form.get('addingKey')?.reset();
        this.form.get('addingValue')?.reset();
    }

    getKeyCssClassPredicate(value: { key: string, value: string | number }, key2: string) {
        if (value != null && stringIsSet(key2) && value.key === key2) {
            return ['background-color-accent', 'usedKey'];
        } else {
            return undefined;
        }
    }

    editKeyValue(value: any) {
        if (value != null) {
            this.form.get('addingKey')?.setValue(value.key);
            this.form.get('addingValue')?.setValue(value.value);
        }
    }

    deleteKeyValue(value: any) {
        const currentValue = this.form.get('value')?.value != null ? {...this.form.get('value')?.value} : {};
        if (value.key in currentValue) {
            delete currentValue[value.key];
        }
        this.form.get('value')?.setValue(currentValue);
    }

    getPossibleValuesPredicate(key: string, pValues: OptionalParameterInfo[]) {
        const option = pValues.find(pValue => pValue.key == key)
        let obj: { type?: typeFormValue, possibleValues?: dataForm[] } = {}
        if (option != null && option.possibleValues) {
            if (option.allowAnyValue) {
                obj.type = typeFormValue.ELENCO_APERTO
                obj.possibleValues = option.possibleValues.map(p => {
                    const pValue: dataForm = {html: p as any, registerValue: p, valueForm: p as any}
                    return pValue
                })
            } else {
                obj.type = typeFormValue.ELENCO_VINCOLATO
                obj.possibleValues = option.possibleValues.map(p => {
                    const pValue: dataForm = {html: p as any, registerValue: p, valueForm: p as any}
                    return pValue
                })
            }
        }
        return obj

    }

    clickSpecialButton(dialogComponent: TemplateRef<any>) {
        const dialogref = this.matDialog.open(dialogComponent, {
            maxWidth: '100vw', autoFocus: false,
            exitAnimationDuration: 0.1
        })
        dialogref.afterClosed().subscribe(() => {
            this.specialButtonForm.reset();
        });
    }

    addKeySpecialValue() {
        const values = deepClone(this.specialButtonForm.value)
        const currentValues = values.currentValues ?? []
        delete values.currentValues
        currentValues.push(values)
        this.specialButtonForm.get('currentValues')?.setValue(currentValues)
        console.log(this.specialButtonForm.value)
    }

    getDataFormPredicate(values: PossibleValuesFormField[]) {
        if (values == null) {
            return []
        }
        return values.map(v => {
            const obj: dataForm = {
                html: v.noTraduction!,
                registerValue: v.registerValue,
                valueForm: v.registerValue,
            }
            return obj
        })
    }

}
